/**
 * Canonical capability matrix — the ONE module-access authority (Phase 217 plan
 * 217-01 / POL-02 / MOD-01..MOD-05).
 *
 * Before this file, four drifted copies of the same "role-or-admin, floored by
 * ativo" rule lived across `comercial.ts`, `AppChrome.tsx`,
 * `return-destination.ts` and the per-route inline gates — and they disagreed
 * (Comercial did not inherit Administração, MOD-01). `resolveCapabilities`
 * collapses that rule into a single pure projection every downstream surface
 * (nav flags, RSC page guards, route `require:` gates, return-destination)
 * derives from.
 *
 * Composition contract (D-1.18 — booleans spelled out, never an implicit
 * super-permission; `s.role.ativo` is ALWAYS the floor):
 *   - administracao  = ativo && administracao                     (MOD-04 admin bit)
 *   - comercial      = ativo && (comercial   || administracao)    (MOD-01)
 *   - marketing      = ativo && (marketing   || administracao)    (MOD-02)
 *   - financeiro     = ativo && (financeiro  || administracao)    (MOD-03)
 *   - controladoria  = ativo && (juridico    || administracao)    (folded in from AppChrome)
 *   - credenciais    = ativo && (credenciais || administracao)    (OS 18083 / D3 — Permissão de Credenciais)
 *   - universal      = ativo
 *   - superAdmin     = ativo && administracao && sede (env-scoped)
 *
 * MOD-04: no departmental role widens `administracao`/`superAdmin` — only the
 * admin bit grants them. MOD-05: combining roles yields the union; no role
 * removes access another grants (the matrix ORs independently per field).
 *
 * TDZ / leaf constraint (POL-02 / RESEARCH Pitfall 6): this module MUST stay an
 * import LEAF. It imports ONLY `./types` (SessionContext) and `./super-admin`
 * (itself a leaf over `./types`). It NEVER imports the nav layer
 * (`nav-config.ts`/`Sidebar.tsx`/`AppChrome.tsx`) — those import the matrix
 * one-way. A back-import would create a cycle and a TDZ crash at next build /
 * worker boot (see project memory on server-only/import-cycle worker crashes).
 */
import { isSuperAdmin } from './super-admin';
import type { SessionContext } from './types';

/**
 * Effective, ativo-floored module access resolved from a session's role
 * snapshot. Every consumer projects its decision off exactly these fields —
 * there is no second projection of the rule anywhere.
 */
export interface CapabilityMatrix {
  /** The active-membership floor (`escritoriousuario.ativo`). */
  ativo: boolean;
  /** Tenant admin (ativo && administracao). Never granted by a departmental role. */
  administracao: boolean;
  /** Sede-scoped super admin. Env-gated; isolated so a misconfig never throws here. */
  superAdmin: boolean;
  /** Comercial module (ativo && (comercial || administracao)) — MOD-01. */
  comercial: boolean;
  /** Marketing module (ativo && (marketing || administracao)) — MOD-02. */
  marketing: boolean;
  /** Financeiro module (ativo && (financeiro || administracao)) — MOD-03. */
  financeiro: boolean;
  /** Controladoria module (ativo && (juridico || administracao)). */
  controladoria: boolean;
  /**
   * Permissão de Credenciais (ativo && (credenciais || administracao)) —
   * OS 18083 / D3. Consulta de Credenciais de Tribunal pelo processo e
   * geração de TOTP; NÃO dá acesso ao cadastro (`administracao` isolado).
   */
  credenciais: boolean;
  /** Universal floor surfaces (Contatos/Tarefas/Documentos): any active member. */
  universal: boolean;
}

/**
 * Project a session into the canonical capability matrix (POL-02).
 *
 * NEVER throws: the sede-scoped `isSuperAdmin` reads `PRINCIPAL_ESCRITORIO_ID`
 * and throws when the env is missing; we wrap that single call in its own
 * try/catch and fall back to `false` so one misconfigured env var can never
 * collaterally deny a legit tenant admin's other modules (T-217-02). Mirrors the
 * per-flag isolation that `AppChrome.tsx` already applied inline.
 */
export function resolveCapabilities(s: SessionContext): CapabilityMatrix {
  const { role } = s;
  const ativo = role.ativo === true;
  const admin = role.administracao === true;

  let superAdmin = false;
  try {
    superAdmin = isSuperAdmin(s);
  } catch {
    // PRINCIPAL_ESCRITORIO_ID misconfig → fail-closed to false; other bits still resolve.
    superAdmin = false;
  }

  return {
    ativo,
    administracao: ativo && admin,
    superAdmin,
    comercial: ativo && (role.comercial === true || admin),
    marketing: ativo && (role.marketing === true || admin),
    financeiro: ativo && (role.financeiro === true || admin),
    controladoria: ativo && (role.juridico === true || admin),
    credenciais: ativo && (role.credenciais === true || admin),
    universal: ativo,
  };
}
